/**
 * AssessmentHistory - Assessment History List View
 *
 * Displays history of all assessments for a product:
 * - List of past assessments with status
 * - Scores and tier achieved
 * - Date created/completed
 * - Link to view full assessment details
 * - Filter by status/date
 *
 * @epic DC-523 Vendor Certification Program
 * @task DC-554 (Phase 5.3)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import '../../styles/vendor.css';

// ============================================================================
// TYPES
// ============================================================================

type AssessmentStatus = 'draft' | 'in_progress' | 'pending_review' | 'approved' | 'rejected' | 'expired';
type CertificationTier = 'platinum' | 'gold' | 'silver' | 'bronze' | null;

interface StationScores {
  data_quality?: number;
  ml_readiness?: number;
  ontology?: number;
  fair?: number;
}

interface Assessment {
  assessment_id: string;
  product_id: string;
  product_name: string;
  vendor_name: string;
  status: AssessmentStatus;
  overall_score?: number;
  station_scores?: StationScores;
  tier_achieved?: CertificationTier;
  files_count: number;
  created_at: string;
  submitted_at?: string;
  completed_at?: string;
  reviewed_by?: string;
  reviewer_notes?: string;
}

interface ProductInfo {
  product_id: string;
  name: string;
  vendor_id: string;
  vendor_name: string;
  category?: string;
  current_tier?: CertificationTier;
  current_certification_expires?: string;
}

// ============================================================================
// CONFIGURATION
// ============================================================================

const STATUS_CONFIG: Record<AssessmentStatus, { label: string; color: string; bgColor: string }> = {
  draft: { label: 'Draft', color: '#6b7280', bgColor: '#f3f4f6' },
  in_progress: { label: 'In Progress', color: '#2563eb', bgColor: '#dbeafe' },
  pending_review: { label: 'Pending Review', color: '#ca8a04', bgColor: '#fef3c7' },
  approved: { label: 'Approved', color: '#16a34a', bgColor: '#dcfce7' },
  rejected: { label: 'Rejected', color: '#dc2626', bgColor: '#fee2e2' },
  expired: { label: 'Expired', color: '#9ca3af', bgColor: '#f3f4f6' },
};

const TIER_COLORS: Record<string, string> = {
  platinum: '#166534',
  gold: '#ca8a04',
  silver: '#6b7280',
  bronze: '#92400e',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function formatDate(dateStr?: string): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTier(tier: CertificationTier): string {
  if (!tier) return '-';
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function getScoreColor(score: number): string {
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#ca8a04';
  return '#dc2626';
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface ProductHeaderProps {
  product: ProductInfo;
  onStartAssessment: () => void;
}

const ProductHeader: React.FC<ProductHeaderProps> = ({ product, onStartAssessment }) => {
  return (
    <div className="assessment-history-product-header">
      <div className="product-info">
        <h2>{product.name}</h2>
        <span className="vendor-name">{product.vendor_name}</span>
        {product.category && <span className="product-category">{product.category}</span>}
      </div>
      <div className="product-status">
        {product.current_tier && (
          <div className="current-tier">
            <span className="tier-label">Current Tier:</span>
            <span
              className="tier-value"
              style={{ color: TIER_COLORS[product.current_tier] }}
            >
              {formatTier(product.current_tier)}
            </span>
          </div>
        )}
        {product.current_certification_expires && (
          <div className="cert-expiry">
            Expires: {formatDate(product.current_certification_expires)}
          </div>
        )}
      </div>
      <button className="btn btn-primary" onClick={onStartAssessment}>
        Start New Assessment
      </button>
    </div>
  );
};

interface FilterBarProps {
  statusFilter: AssessmentStatus | 'all';
  onStatusChange: (status: AssessmentStatus | 'all') => void;
  sortBy: 'date' | 'score';
  onSortChange: (sort: 'date' | 'score') => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  statusFilter,
  onStatusChange,
  sortBy,
  onSortChange,
}) => {
  return (
    <div className="assessment-history-filters">
      <div className="filter-group">
        <label>Status:</label>
        <select
          value={statusFilter}
          onChange={(e) => onStatusChange(e.target.value as AssessmentStatus | 'all')}
        >
          <option value="all">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="in_progress">In Progress</option>
          <option value="pending_review">Pending Review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="expired">Expired</option>
        </select>
      </div>
      <div className="filter-group">
        <label>Sort by:</label>
        <select value={sortBy} onChange={(e) => onSortChange(e.target.value as 'date' | 'score')}>
          <option value="date">Date (Newest First)</option>
          <option value="score">Score (Highest First)</option>
        </select>
      </div>
    </div>
  );
};

interface AssessmentCardProps {
  assessment: Assessment;
  onClick: () => void;
}

const AssessmentCard: React.FC<AssessmentCardProps> = ({ assessment, onClick }) => {
  const statusConfig = STATUS_CONFIG[assessment.status];

  return (
    <div className="assessment-history-card" onClick={onClick}>
      <div className="assessment-card-header">
        <div className="assessment-card-status">
          <span
            style={{
              display: 'inline-block',
              padding: '3px 10px',
              borderRadius: '4px',
              backgroundColor: statusConfig.bgColor,
              color: statusConfig.color,
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            {statusConfig.label}
          </span>
        </div>
        <div className="assessment-card-date">
          {formatDate(assessment.completed_at || assessment.submitted_at || assessment.created_at)}
        </div>
      </div>

      <div className="assessment-card-body">
        <div className="assessment-card-scores">
          {assessment.overall_score !== undefined ? (
            <div className="overall-score">
              <span
                className="score-value"
                style={{ color: getScoreColor(assessment.overall_score) }}
              >
                {assessment.overall_score}%
              </span>
              <span className="score-label">Overall Score</span>
            </div>
          ) : (
            <div className="no-score">Not scored yet</div>
          )}

          {assessment.tier_achieved && (
            <div className="tier-achieved">
              <span
                className="tier-badge"
                style={{ color: TIER_COLORS[assessment.tier_achieved] }}
              >
                {formatTier(assessment.tier_achieved)}
              </span>
            </div>
          )}
        </div>

        {assessment.station_scores && (
          <div className="station-scores-mini">
            {Object.entries(assessment.station_scores).map(([station, score]) => (
              <div key={station} className="station-mini">
                <span className="station-name">{station.replace('_', ' ')}</span>
                <div className="station-bar">
                  <div
                    className="station-bar-fill"
                    style={{
                      width: `${score}%`,
                      backgroundColor: getScoreColor(score || 0),
                    }}
                  />
                </div>
                <span className="station-score">{score}%</span>
              </div>
            ))}
          </div>
        )}

        <div className="assessment-card-meta">
          <span className="files-count">{assessment.files_count} files</span>
          {assessment.reviewed_by && (
            <span className="reviewer">Reviewed by: {assessment.reviewed_by}</span>
          )}
        </div>
      </div>

      <div className="assessment-card-footer">
        <button className="btn btn-link">View Details →</button>
      </div>
    </div>
  );
};

interface TimelineViewProps {
  assessments: Assessment[];
  onAssessmentClick: (id: string) => void;
}

const TimelineView: React.FC<TimelineViewProps> = ({ assessments, onAssessmentClick }) => {
  return (
    <div className="assessment-timeline">
      {assessments.map((assessment, index) => (
        <div key={assessment.assessment_id} className="timeline-item">
          <div className="timeline-line">
            <div
              className="timeline-dot"
              style={{
                backgroundColor: STATUS_CONFIG[assessment.status].color,
              }}
            />
            {index < assessments.length - 1 && <div className="timeline-connector" />}
          </div>
          <AssessmentCard
            assessment={assessment}
            onClick={() => onAssessmentClick(assessment.assessment_id)}
          />
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const AssessmentHistory: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [product, setProduct] = useState<ProductInfo | null>(null);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<AssessmentStatus | 'all'>(
    (searchParams.get('status') as AssessmentStatus) || 'all'
  );
  const [sortBy, setSortBy] = useState<'date' | 'score'>(
    (searchParams.get('sort') as 'date' | 'score') || 'date'
  );

  // Fetch product and assessments
  const fetchData = useCallback(async () => {
    if (!productId) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch product info
      const productRes = await fetch(`/api/products/${productId}`);
      if (!productRes.ok) {
        throw new Error('Failed to fetch product');
      }
      const productData = await productRes.json();
      setProduct(productData);

      // Fetch assessments
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      params.set('sort', sortBy);

      const assessmentRes = await fetch(
        `/api/products/${productId}/assessments?${params.toString()}`
      );
      if (!assessmentRes.ok) {
        throw new Error('Failed to fetch assessments');
      }
      const assessmentData = await assessmentRes.json();
      setAssessments(assessmentData.assessments || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [productId, statusFilter, sortBy]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (sortBy !== 'date') params.set('sort', sortBy);
    setSearchParams(params);
  }, [statusFilter, sortBy, setSearchParams]);

  // Handle start new assessment
  const handleStartAssessment = () => {
    navigate(`/vendors/assessment/new?product_id=${productId}`);
  };

  // Handle view assessment
  const handleViewAssessment = (assessmentId: string) => {
    navigate(`/vendors/assessment/${assessmentId}`);
  };

  // Filter and sort assessments (client-side backup)
  const filteredAssessments = assessments
    .filter((a) => statusFilter === 'all' || a.status === statusFilter)
    .sort((a, b) => {
      if (sortBy === 'score') {
        return (b.overall_score || 0) - (a.overall_score || 0);
      }
      return (
        new Date(b.completed_at || b.created_at).getTime() -
        new Date(a.completed_at || a.created_at).getTime()
      );
    });

  if (loading) {
    return (
      <div className="assessment-history-page">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading assessment history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="assessment-history-page">
        <div className="error-state">
          <h2>Error</h2>
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={fetchData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="assessment-history-page">
        <div className="not-found-state">
          <h2>Product Not Found</h2>
          <p>The requested product could not be found.</p>
          <button className="btn btn-secondary" onClick={() => navigate('/vendors')}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-history-page">
      {/* Header */}
      <div className="page-header">
        <button className="btn btn-back" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1>Assessment History</h1>
      </div>

      {/* Product Info */}
      <ProductHeader product={product} onStartAssessment={handleStartAssessment} />

      {/* Filters */}
      <FilterBar
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        sortBy={sortBy}
        onSortChange={setSortBy}
      />

      {/* Assessment List */}
      <div className="assessment-history-content">
        {filteredAssessments.length === 0 ? (
          <div className="empty-state">
            <h3>No Assessments Found</h3>
            <p>
              {statusFilter !== 'all'
                ? `No assessments with status "${STATUS_CONFIG[statusFilter].label}" found.`
                : 'This product has no assessments yet.'}
            </p>
            {statusFilter === 'all' && (
              <button className="btn btn-primary" onClick={handleStartAssessment}>
                Start First Assessment
              </button>
            )}
          </div>
        ) : (
          <TimelineView
            assessments={filteredAssessments}
            onAssessmentClick={handleViewAssessment}
          />
        )}
      </div>

      {/* Stats Summary */}
      {assessments.length > 0 && (
        <div className="assessment-stats-summary">
          <div className="stat-item">
            <span className="stat-value">{assessments.length}</span>
            <span className="stat-label">Total Assessments</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {assessments.filter((a) => a.status === 'approved').length}
            </span>
            <span className="stat-label">Approved</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {assessments.filter((a) => a.status === 'in_progress' || a.status === 'draft').length}
            </span>
            <span className="stat-label">In Progress</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {assessments.length > 0
                ? Math.round(
                    assessments
                      .filter((a) => a.overall_score !== undefined)
                      .reduce((sum, a) => sum + (a.overall_score || 0), 0) /
                      assessments.filter((a) => a.overall_score !== undefined).length
                  ) || '-'
                : '-'}
              %
            </span>
            <span className="stat-label">Avg Score</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentHistory;
